<template>
  <div id="award-type-list" class="premium-dashboard">
    <!-- Background Floating Light Orbs -->
    <div class="glow-orb orb-1"></div>
    <div class="glow-orb orb-2"></div>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons -->
      <v-icon class="deco-icon icon-card" color="rgba(255, 255, 255, 0.08)"
        >mdi-trophy-outline</v-icon
      >
      <v-icon class="deco-icon icon-receipt" color="rgba(255, 255, 255, 0.08)"
        >mdi-award-outline</v-icon
      >
      <v-icon class="deco-icon icon-safe" color="rgba(255, 255, 255, 0.08)"
        >mdi-shield-star-outline</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <br />
      <v-row class="ma-0 align-center relative-content">
        <v-col cols="12" md="7" class="pa-6 pb-12">
          <div class="d-flex align-center title-container">
            <v-icon large color="white" class="mr-3 title-icon pulse-animation"
              >mdi-trophy-variant</v-icon
            >
            <h1 class="hero-title animate-title">
              {{ PageTitle || "Award Types" }}
            </h1>
          </div>
          <p class="hero-subtitle animate-subtitle">
            {{
              PageDescription ||
              "Configure, manage, and link award criteria for members and LOs."
            }}
          </p>
        </v-col>
        <v-col
          cols="12"
          md="5"
          class="pa-6 pb-12 text-md-right d-none d-sm-block"
        >
          <v-btn
            @click.prevent="addRecordPrompt = true"
            color="white"
            light
            elevation="3"
            rounded
            large
            class="font-weight-bold mr-3"
          >
            <v-icon left color="primary">mdi-plus</v-icon>
            Create Award Type
          </v-btn>
          <v-btn
            large
            @click.prevent="refreshPageData"
            color="rgba(255,255,255,0.2)"
            dark
            elevation="0"
            rounded
            class="font-weight-bold"
            style="border: 1px solid rgba(255, 255, 255, 0.4)"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Summary Statistics Row -->
    <v-row class="mb-6" v-if="tableData1.length > 0">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-total"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total Awards</span>
              <h2 class="stat-value">{{ tableData1.length }}</h2>
            </div>
            <v-avatar color="rgba(21, 101, 192, 0.1)" size="48">
              <v-icon color="#1565C0">mdi-trophy-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-success"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Active Awards</span>
              <h2 class="stat-value">
                {{
                  tableData1.filter((item) => item.ActiveStatusTxt === "Active")
                    .length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(76, 175, 80, 0.1)" size="48">
              <v-icon color="#4caf50">mdi-check-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-rev"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Member Category</span>
              <h2 class="stat-value">
                {{
                  tableData1.filter(
                    (item) =>
                      item.AwardCategoryName &&
                      item.AwardCategoryName.includes("Member")
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(30, 136, 229, 0.1)" size="48">
              <v-icon color="#1e88e5">mdi-account-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-pending"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">LO Category</span>
              <h2 class="stat-value">
                {{
                  tableData1.filter(
                    (item) =>
                      item.AwardCategoryName &&
                      (item.AwardCategoryName.includes("LO") ||
                        item.AwardCategoryName.includes("LOM"))
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(255, 152, 0, 0.1)" size="48">
              <v-icon color="#ff9800">mdi-office-building</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="filter-card glass-card mb-6">
      <div class="filter-card-header d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <h3 class="filter-card-title">Search Parameters</h3>
      </div>

      <v-card-text class="pa-6">
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <v-row wrap align="center">
            <v-col cols="12" sm="8" md="8" lg="4" class="py-2">
              <label class="filter-label"
                ><span class="text-danger">* </span>Award Category</label
              >
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="AwardCategoryOptions"
                :rules="AwardCategoryRules"
                v-model="AwardCategory"
                placeholder="Select Award Category"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="4" md="4" lg="4" class="py-2 text-right pt-6">
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SubmitFlag"
                rounded
                large
                class="filter-btn btn-search white--text mr-2"
              >
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
              <v-btn
                @click.prevent="
                  resetForm();
                  AwardCategory = '';
                "
                rounded
                large
                outlined
                class="filter-btn btn-clear"
              >
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Loading Shimmer State -->
    <v-card v-if="SubmitFlag" class="glass-card pa-6 mb-6">
      <v-skeleton-loader
        type="table-heading, table-thead, table-tbody, table-tfoot"
        class="premium-loader"
      ></v-skeleton-loader>
    </v-card>

    <!-- Data Table & Search Grid -->
    <v-card
      v-if="!SubmitFlag && tableData1.length > 0"
      class="table-container-card glass-card"
    >
      <v-card-title
        class="px-6 py-4 d-flex justify-content-between align-center flex-wrap"
      >
        <div class="d-flex align-center mb-2 mb-sm-0">
          <v-icon color="#1565C0" class="mr-2">mdi-database</v-icon>
          <span class="result-count-text"
            >{{ tableData1.length }} Award Types Found</span
          >
        </div>

        <div class="d-flex align-center flex-wrap">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Quick search table..."
            single-line
            hide-details
            dense
            outlined
            rounded
            class="global-table-search"
          ></v-text-field>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          class="premium-table"
          v-model="selected1"
          :search="search"
          :item-key="tableOptions1.ItemKey"
          :single-select="tableOptions1.SingleSelectFlag"
          :show-select="tableOptions1.ShowSelectFlag"
          :headers="tableColumns1"
          :items="tableData1"
          :items-per-page="tableOptions1.ItemsPerPage"
          :footer-props="tableOptions1.FooterProps"
        >
          <template v-slot:item.ActiveStatusTxt="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="
                item.ActiveStatusTxt === 'Active'
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(255, 152, 0, 0.1)'
              "
              :text-color="
                item.ActiveStatusTxt === 'Active' ? '#2e7d32' : '#e65100'
              "
            >
              <v-icon
                left
                x-small
                :color="
                  item.ActiveStatusTxt === 'Active' ? '#2e7d32' : '#e65100'
                "
              >
                {{
                  item.ActiveStatusTxt === "Active"
                    ? "mdi-check-circle"
                    : "mdi-clock-outline"
                }}
              </v-icon>
              {{ item.ActiveStatusTxt }}
            </v-chip>
          </template>

          <template v-slot:item.Actions="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom v-if="item.PreviewFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.prevent="previewAlert(item)"
                  >
                    <v-icon small>mdi-file-search</v-icon>
                  </v-btn>
                </template>
                <span>Preview</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.EditButtonFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="warning"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.prevent="editAlert(item)"
                  >
                    <v-icon small>mdi-file-document-edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.DeleteButtonFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.prevent="deleteAlert(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.MapCriteriaButtonFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.prevent="openMapCriteriaDialog(item)"
                  >
                    <v-icon small>mdi-link-variant</v-icon>
                  </v-btn>
                </template>
                <span>Map Criteria</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addRecordPrompt" persistent max-width="75%">
      <award-type-create
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"
      ></award-type-create>
    </v-dialog>

    <v-dialog v-model="editRecordPrompt" persistent max-width="75%">
      <award-type-edit
        :editRecordPrompt="editRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt($event)"
        :AwardTypeId="recordData.AwardTypeId"
        v-if="editRecordPrompt"
      ></award-type-edit>
    </v-dialog>

    <v-dialog v-model="mapCriteriaDialog" persistent max-width="70%">
      <award-criteria-mapping
        :mapCriteriaPrompt="mapCriteriaDialog"
        @hideMapCriteriaPrompt="mapCriteriaDialog = false"
        :AwardTypeId="recordData.AwardTypeId"
        :AwardTypeTitle="recordData.AwardTypeTitle"
        v-if="mapCriteriaDialog"
      ></award-criteria-mapping>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import AwardTypeCreate from "@/view/pages/erp/jci-award/award-type/AwardTypeCreate.vue";
import AwardTypeEdit from "@/view/pages/erp/jci-award/award-type/AwardTypeEdit.vue";
import AwardCriteriaMapping from "@/view/pages/erp/jci-award/award-type/AwardCriteriaMapping.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    AwardTypeCreate,
    AwardTypeEdit,
    AwardCriteriaMapping,
  },
  data() {
    return {
      valid1: true,
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,

      SelectFlag: true,
      DeleteFlag: false,
      SubmitFlag: false,

      alert: {},

      valid: true,

      RoleId: "",

      ResultFlag: 0,

      AwardCategoryRules: [(v) => !!v || "Award Category is required"],
      AwardCategory: "",
      AwardCategoryOptions: [],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      mapCriteriaDialog: false,

      recordData: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.getAwardCategoryOptions();
      }
    },
    AwardCategoryOptions: function () {
      console.log("watch AwardCategoryOptions");
      this.AwardCategoryOptionsLoading = false;
    },
    AwardCategory: function () {
      console.log("AwardCategory called");
      this.resetForm();
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewAlert(tr) {
      console.log("previewAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.recordData = tr;
        this.previewRecordPrompt = true;
      } else {
        var message = "Kindly select one record to preview";
        this.errorMessage(message);
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    editAlert(tr) {
      console.log("editAlert called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.recordData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    resetForm() {
      console.log("resetForm called");
      this.rows = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "award_type",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getAwardCategoryOptions() {
      console.log("getAwardCategoryOptions called");
      var selectbox1_source = "AwardCategory";
      var selectbox1_destination = "AwardCategoryOptions";
      var selectbox1_url = "api/award-category/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.tableData1 = [];
        this.selected1 = [];
        this.SubmitFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/lists";
        var upload = {
          UserInterface: 1,
          AwardCategory: this.AwardCategory,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        // thisIns.ProgessStart = 1;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.ProgessStart = 0;
            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            var options = records.TableOptions;

            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;

              thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
          });
      } else {
        var message = "Kindy fill required fields and then submit";
        this.toast("error", message);
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      console.log("tr=" + JSON.stringify(tr));
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert();
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert() {
      console.log("confirmAlert called");
      var tr = this.selectedData;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/>Award Category <b> " + tr.AwardCategoryName + "</b>";
        htmlTxt +=
          "<br/>Award Type Group <b> " + tr.AwardGroupDisplayText + "</b>";
        htmlTxt += "<br/>Award Type Number <b> " + tr.AwardTypeNumber + "</b>";
        htmlTxt += "<br/>Award Type Title <b> " + tr.AwardTypeTitle + "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    deleteRecord(tr) {
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/destroy";
        var upload = {
          UserInterface: 1,
          AwardType: tr.AwardTypeId,
          AwardTypeTitle: tr.AwardTypeTitle,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    openMapCriteriaDialog(tr) {
      console.log("openMapCriteriaDialog called", tr);
      this.recordData = tr;
      this.mapCriteriaDialog = true;
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dashboard {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f6f8fc;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 12px;
}

/* Floating light ambient background orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.45;
  z-index: 0;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #42a5f5 0%, rgba(21, 101, 192, 0) 70%);
  top: -100px;
  right: -50px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(21, 101, 192, 0.2) 0%,
    rgba(66, 165, 245, 0) 70%
  );
  bottom: -150px;
  left: -100px;
}

/* Massive Hero Dashboard Header styling */
.hero-card {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 600px;
  font-weight: 400;
}

/* Glassmorphism General Cards styling */
.glass-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.04) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Summary Statistics Cards */
.stat-card {
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.12) !important;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  filter: blur(40px);
  top: -40px;
  right: -40px;
  opacity: 0.15;
}

.orb-total {
  background-color: #1565c0;
}

.orb-rev {
  background-color: #4caf50;
}

.orb-success {
  background-color: #1e88e5;
}

.orb-pending {
  background-color: #ff9800;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #263238;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

/* Advanced Filter Card header gradient */
.filter-card-header {
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  padding: 16px 24px;
  color: white;
}

.filter-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

/* Autocomplete/Select box rounded controls */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
}

/* Filter action buttons */
.filter-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-clear {
  color: #455a64 !important;
  border: 1.5px solid rgba(69, 90, 100, 0.2) !important;
}

.btn-clear:hover {
  background-color: rgba(69, 90, 100, 0.05) !important;
  border-color: #455a64 !important;
}

.btn-search {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
  border: none !important;
}

.btn-search:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}

/* Skeleton Loaders and Empty States styling */
.premium-loader {
  background: transparent !important;
}

/* Data grid container and table styles */
.table-container-card {
  overflow: hidden;
}

.result-count-text {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

/* Premium global table search text field */
.global-table-search {
  max-width: 280px;
}

.global-table-search >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: rgba(21, 101, 192, 0.01) !important;
}

.global-table-search.v-input--is-focused
  >>> .v-input__control
  > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
}

/* Real premium tables */
.premium-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.premium-table >>> thead > tr > th {
  background-color: rgba(21, 101, 192, 0.04) !important;
  color: #1565c0 !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  border-bottom: 2px solid rgba(21, 101, 192, 0.08) !important;
}

.premium-table >>> tbody > tr {
  transition: background-color 0.2s ease;
}

.premium-table >>> tbody > tr:hover {
  background-color: rgba(21, 101, 192, 0.03) !important;
}

.premium-table >>> tbody > tr > td {
  border-bottom: 1px solid rgba(21, 101, 192, 0.04) !important;
  font-size: 15px !important;
  color: #37474f;
  padding: 12px 16px !important;
}

.status-chip {
  font-weight: bold;
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }

  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Hero header animations & shapes */
.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Decorative background icons */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-card {
  font-size: 110px !important;
  right: 20%;
  top: -20px;
  transform: rotate(15deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-receipt {
  font-size: 80px !important;
  left: 10%;
  bottom: -15px;
  transform: rotate(-10deg);
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.icon-safe {
  font-size: 90px !important;
  right: 5%;
  bottom: -10px;
  transform: rotate(20deg);
  animation: floatDeco 10s infinite ease-in-out;
}

/* Specific text and icon animations */
.animate-title {
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-subtitle {
  opacity: 0;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

/* Keyframes definitions */
@keyframes shineSweep {
  0% {
    left: -120%;
  }

  15%,
  100% {
    left: 120%;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }

  50% {
    transform: translateY(-12px) rotate(20deg) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }

  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  }
}

.action-btn-view {
  transition: transform 0.2s ease;
}

.action-btn-view:hover {
  transform: scale(1.18);
  background: rgba(21, 101, 192, 0.08) !important;
}
</style>
